import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { FormLabel, CircularProgress, Autocomplete, Button } from '@mui/joy';
import _ from 'lodash';
import { readAccounts } from '../API/account';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

export default function AccountOptionList({
    label,
    required,
    currentAccount,
    setCurrentAccount,
    showActiveOnly = true,
    disabled = false,
    variant = 'default',
    sx = {},
}) {
    const { t } = useTranslation('ui');
    const { hasPermission } = usePermissions();
    const canListAccounts = hasPermission(PERMISSIONS.ACCOUNTS_LIST);
    const canCreateAccount = hasPermission(PERMISSIONS.ACCOUNTS_CREATE);
    const resolvedLabel = label ?? t('widgets.account');
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const getAccounts = useCallback(async (searchTerm) => {
        if (!canListAccounts) {
            setOptions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await readAccounts(searchTerm || '', '');
            const results = response?.data?.results;
            let accounts = Array.isArray(results) ? results : [];

            if (showActiveOnly) {
                accounts = accounts.filter(account => account.is_active === true);
            }

            setOptions(accounts);
        } catch (error) {
            console.error('Failed to fetch accounts:', error.message);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [showActiveOnly, canListAccounts]);

    const debouncedGetAccounts = useCallback(_.debounce(getAccounts, 300), [getAccounts]);

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
        if (!newInputValue) {
            getAccounts('');
        }
    };

    useEffect(() => {
        if (inputValue) {
            debouncedGetAccounts(inputValue);
        }
    }, [inputValue, debouncedGetAccounts]);

    useEffect(() => {
        if (open) {
            getAccounts('');
        }
    }, [open, getAccounts]);

    // Format account label for display
    const getAccountLabel = (account) => {
        if (!account) return '';
        let displayLabel = account.name || '';
        if (account.bank_name) {
            displayLabel += ` - ${account.bank_name}`;
        }
        if (account.account_number) {
            displayLabel += ` (${account.account_number})`;
        }
        return displayLabel;
    };

    const handleChange = (event, value) => {
        if (setCurrentAccount) {
            setCurrentAccount(value);
        }
    };

    const boxSx = {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        position: 'relative',
        '&:focus-within': {
            boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
            borderRadius: '8px',
        },
        ...(variant === 'filter' ? { marginBottom: 0 } : { marginBottom: 2 }),
        ...sx,
    };

    return (
        <Box sx={boxSx}>
            <FormLabel
                sx={{
                    fontWeight: 600,
                    color: '#555',
                    marginBottom: '6px',
                    fontSize: '14px',
                }}
            >
                {required ? `${resolvedLabel} *` : resolvedLabel}
            </FormLabel>
            <Autocomplete
                onInputChange={handleInputChange}
                value={currentAccount}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                placeholder={t('widgets.selectAccount')}
                options={Array.isArray(options) ? options : []}
                loading={loading}
                disabled={disabled || !canListAccounts}
                onChange={handleChange}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => getAccountLabel(option)}
                noOptionsText={
                    loading ? (
                        <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
                    ) : !canListAccounts ? (
                        t('permissions.noPermission', {
                            defaultValue: 'You do not have permission to perform this action.',
                        })
                    ) : options.length === 0 && canCreateAccount ? (
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            px={2}
                        >
                            <span>{t('widgets.noAccountsFound')}</span>
                            <Button
                                variant="solid"
                                color="primary"
                                size="sm"
                                onClick={() => window.open('/settings/accounts', '_blank')}
                            >
                                {t('widgets.addAccount')}
                            </Button>
                        </Box>
                    ) : options.length === 0 ? (
                        <span>{t('widgets.noAccountsFound')}</span>
                    ) : (
                        t('widgets.typeToSearchAccounts')
                    )
                }
                loadingText={t('actions.loading')}
                filterOptions={(opts) => opts}
                endDecorator={
                    loading && (
                        <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
                    )
                }
                sx={{
                    backgroundColor: '#f7f9fc',
                    borderRadius: '8px',
                    padding: '4px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    '&:hover': { borderColor: '#5f09c2' },
                    '&:focus': {
                        borderColor: '#5f09c2',
                        outline: 'none',
                        boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
                    },
                    '&[disabled]': {
                        backgroundColor: '#e9ecef',
                        color: '#6c757d',
                        cursor: 'not-allowed',
                    },
                }}
            />
        </Box>
    );
}

AccountOptionList.propTypes = {
    label: PropTypes.string,
    required: PropTypes.bool,
    currentAccount: PropTypes.object,
    setCurrentAccount: PropTypes.func,
    showActiveOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'filter']),
    sx: PropTypes.object,
};
